/**
 * 版本更新检测器
 *
 * 检测时机：
 *   1. 页面加载（init 时立即检测）
 *   2. 5 分钟定时轮询
 *   3. 路由切换（afterEach）
 *   4. 页面从后台切回前台（visibilitychange）
 *
 * 检测逻辑：
 *   调用后台 API 获取最新版本配置，比较 package.json version 与配置版本。
 *   当前版本 > 配置版本：无提示
 *   当前版本 ≤ 配置版本：按 updateType 分发 (0=强提示, 1=中提示, 2=静默)
 */

import { versionLastVersion, versionAddUserBrowse } from '@/jmHttp/api/common';
import { getStorageAuthInfo } from '@/utils/storageOperateApi';
import { LOCAL_STORAGE_KEY } from '@/constants';
import { compareVersion, isValidVersion } from './versionCompare';
import { VersionReadState } from './versionReadState';
import { UPDATE_TYPE, getAppVersion, isRouteInWhitelist } from './versionConfig';
import router from '@/router';

class VersionChecker {
  constructor(options = {}) {
    this.options = {
      currentVersion: options.currentVersion || getAppVersion(),
      pollingInterval: options.pollingInterval || 5 * 60 * 1000,
      maxRetries: options.maxRetries || 3,
      onUpdate: options.onUpdate || null,
      onError: options.onError || null
    };

    logger.log('当前版本号:', this.options.currentVersion);

    this.pollingTimer = null;
    this.isChecking = false;
    this.retryCount = 0;
    this.lastCheckTime = 0;
    this.lastVersionConfig = null;
    this.pendingUpdate = null; // 白名单页面暂存的更新信息
    this.authExpired = false; // 会话失效后置真，停止后续一切版本检测（重新登录后由 resetAuthState 恢复）
    this._authExpiredToken = null; // 记录会话失效时的 webtoken，用于判定是否已重新登录

    this.isPageVisible = !document.hidden;
    this._boundVisibility = this._handleVisibilityChange.bind(this);

    this._readState = null; // 延迟初始化（需要 uid）
  }

  /**
   * 启动检测器（需在用户登录后调用）
   */
  init() {
    this._initReadState();
    this._bindVisibilityChange();
    this.check();
    this._startPolling();
  }

  // ──────────────────────────────── 核心检测 ────────────────────────────────

  async check() {
    // 会话已失效：拦截器已弹出重新登录提示，再轮询只会持续 201/401，直接跳过避免每 5 分钟刷错误日志
    if (this.authExpired) return;
    // 用户没登录时，不检测
    const userInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
    if (!userInfo.uid) return;
    // 白名单页面不请求版本接口（与路由 afterEach 中 isRouteInWhitelist 一致）
    const currentRoute = router.currentRoute?.value;
    if (currentRoute && isRouteInWhitelist(currentRoute)) {
      return;
    }
    if (this.isChecking) return;
    this.isChecking = true;

    try {
      const config = await this._fetchLatestVersion();
      if (!config) return;

      this.lastVersionConfig = config;
      this.lastCheckTime = Date.now();
      this.retryCount = 0;

      const currentVersion = this.options.currentVersion;
      if (!isValidVersion(currentVersion)) return;

      const cmp = compareVersion(currentVersion, config.versionCode);

      // 当前版本 > 配置版本 → 无提示
      if (cmp > 0) return;

      // 当前版本 ≤ 配置版本 → 需要按 updateType 处理
      this._handleVersionUpdate(config);
    } catch (error) {
      // 会话失效（登录态过期）：再轮询也无意义，停止轮询并打标记，避免持续刷 201 错误日志
      if (this._isAuthExpiredError(error)) {
        this.authExpired = true;
        // 记录失效时的会话标识，供 resetAuthState 判定是否已重新登录（webtoken 变化即视为重新登录）
        const expiredUser = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
        this._authExpiredToken = expiredUser.webtoken || null;
        this.retryCount = 0;
        this._stopPolling();
        return;
      }
      // 普通失败（如网络抖动）：累计到 maxRetries 才上报一次错误，期间继续轮询重试
      this.retryCount++;
      if (this.retryCount >= this.options.maxRetries) {
        this.retryCount = 0;
        this.options.onError?.(error);
      }
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * 判断错误是否为会话失效（登录态过期）类错误。
   * jmHttp 拦截器对这类错误 reject 的对象上带有 errorcode/code（如 201）。
   * @param {{ errorcode?: number|string, code?: number|string }} [error] - 检测请求 reject 的错误对象
   * @returns {boolean} 命中会话失效错误码返回 true
   */
  _isAuthExpiredError(error) {
    // 201/20101/20102 为登录态失效，401/403 为鉴权失败，均需重新登录
    const AUTH_EXPIRED_CODES = [201, 20101, 20102, 401, 403];
    const code = Number(error?.errorcode ?? error?.code);
    return AUTH_EXPIRED_CODES.includes(code);
  }

  /**
   * 路由切换时调用 — 检测 + 释放白名单暂存
   */
  checkOnRouteChange(isWhitelist = false) {
    if (isWhitelist) {
      // 目标路由在白名单，暂不处理
      return;
    }

    // 路由离开白名单后，释放暂存的更新
    if (this.pendingUpdate) {
      const pending = this.pendingUpdate;
      this.pendingUpdate = null;
      this._dispatchUpdate(pending);
      return;
    }

    this.check();
  }

  /**
   * 重新登录成功后调用：若此前因会话失效停止了检测，则重置标记并恢复轮询。
   *
   * SPA 内通过路由导航重新登录（非整页刷新）时，VersionChecker 实例不会重建，
   * authExpired 标记会一直拦截 check()，导致版本检测永久失效。本方法通过比对
   * 当前存储的 webtoken 与失效时记录的 token，确保只有真正重新登录（会话已更新）
   * 才恢复检测，避免仍处于失效态时立即再次触发 201/401 刷错误日志。
   * @returns {void}
   */
  resetAuthState() {
    // 未处于会话失效态，无需恢复
    if (!this.authExpired) return;
    const userInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
    // 未登录，或仍是失效时的同一会话（webtoken 未变化）：尚未重新登录，保持停止状态
    if (!userInfo.uid || userInfo.webtoken === this._authExpiredToken) return;
    this.authExpired = false;
    this._authExpiredToken = null;
    this.retryCount = 0;
    this._startPolling();
    this.check();
  }

  // ──────────────────────────────── 版本处理 ────────────────────────────────

  _handleVersionUpdate(config) {
    this._initReadState();

    // 已读检查
    if (this._readState && !this._readState.shouldNotify(config)) {
      return;
    }

    this._dispatchUpdate(config);
  }

  _dispatchUpdate(config) {
    if (!this.options.onUpdate) return;

    const updateType = config.updateType;

    // 静默更新 — 不弹框，不上报
    if (updateType === UPDATE_TYPE.SILENT) {
      // 标记已读（前端本地）
      this._readState?.markAsRead(config);
      this.options.onUpdate({ ...config, _silent: true });
      return;
    }

    // 强提示 / 中提示 — 弹框后上报
    this.options.onUpdate({ ...config, _silent: false });
  }

  /**
   * 弹框弹出后由外部调用，标记已读并上报后台
   * updateType=0 或 1 时调用
   */
  markAsReadAndReport(versionConfig) {
    this._initReadState();
    this._readState?.markAsRead(versionConfig);
    // updateType 0/1 上报，2 不上报
    if (versionConfig.updateType !== UPDATE_TYPE.SILENT) {
      this._reportBrowse(versionConfig);
    }
  }

  /**
   * 暂存更新（白名单页面使用）
   */
  setPendingUpdate(config) {
    this.pendingUpdate = config;
  }

  // ──────────────────────────────── API ────────────────────────────────

  async _fetchLatestVersion() {
    const res = await versionLastVersion();
    return res.data || null;
  }

  async _reportBrowse(versionConfig) {
    try {
      await versionAddUserBrowse({
        contentType: 3,
        contentId: versionConfig.id,
        updateType: versionConfig.updateType
      });
    } catch {
      // 上报失败不影响主流程
    }
  }

  // ──────────────────────────────── 轮询 & 生命周期 ────────────────────────────────

  _startPolling() {
    this._stopPolling();
    this.pollingTimer = setInterval(() => {
      this.check();
    }, this.options.pollingInterval);
  }

  _stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  _bindVisibilityChange() {
    document.addEventListener('visibilitychange', this._boundVisibility);
  }

  _handleVisibilityChange() {
    const isVisible = !document.hidden;
    if (isVisible && !this.isPageVisible) {
      setTimeout(() => this.check(), 1000);
    }
    this.isPageVisible = isVisible;
  }

  _initReadState() {
    if (this._readState) return;
    try {
      const userInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
      if (userInfo.uid) {
        this._readState = new VersionReadState(userInfo.uid);
      }
    } catch {
      // ignore
    }
  }

  /**
   * 获取 VersionReadState 实例（给外部组件读取 neverRemind 使用）
   */
  getReadState() {
    this._initReadState();
    return this._readState;
  }

  destroy() {
    this._stopPolling();
    document.removeEventListener('visibilitychange', this._boundVisibility);
  }

  getStatus() {
    return {
      isChecking: this.isChecking,
      lastCheckTime: this.lastCheckTime,
      lastVersionConfig: this.lastVersionConfig,
      currentVersion: this.options.currentVersion,
      retryCount: this.retryCount
    };
  }
}

export default VersionChecker;
